import { useContext, useEffect, useState } from 'react';
import { InventoryAddProductFormContext } from '../../../Context/InventoryAddProductFormContext';
import { showToast } from '../../../Components/Toaster/Toaster';
import Info from '../../../Assets/Info.svg';
import { toCamelCase } from '../../../lib/utils/utils';
import Input from '../../../Components/Input/Input';

function Inventory({ selectedCategory1, selectedCategory2 }) {
    const {
        inventory,
        inventoryInputs,
        setInventoryInputs,
        sizeVariants,
        setSizeVariants,
        colorVariants,
        setColorVariants,
        setInventory,
        formErrors,
        specifications,
        inventoryState,
        setInventoryState,
    } = useContext(InventoryAddProductFormContext);

    const [displayStockTooltip, setDisplayStockTooltip] = useState(false);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if(specifications.size && specifications.color) {
            let sizeObject = {
                size: specifications.size,
                id: toCamelCase(specifications.size),
                mrp: '',
                sellingPrice: '',
                primary: true
            };

            let colorObject = {
                color: specifications.color,
                id: toCamelCase(specifications.color),
                variantName: '',
                images: [],
                primary: true
            };

            if (sizeVariants.some(variant => variant.primary)) {
                const notPrimaries = sizeVariants.filter(variant => !variant.primary);
                const filteredArray = sizeVariants.filter(variant => variant.primary);

                setSizeVariants([
                    ...notPrimaries,
                    { ...filteredArray[0], size: specifications.size, id: toCamelCase(specifications.size) }
                ]);
            }
            else {
                setSizeVariants([ ...sizeVariants, sizeObject ]);
            }

            if (colorVariants.some(variant => variant.primary)) {
                const notPrimaries = colorVariants.filter(variant => !variant.primary);
                const filteredArray = colorVariants.filter(variant => variant.primary);

                setColorVariants([
                    ...notPrimaries,
                    { ...filteredArray[0], color: specifications.color, id: toCamelCase(specifications.color) }
                ]);
            }
            else {
                setColorVariants([ ...colorVariants, colorObject ]);
            }
        }
        else if(specifications.size && !specifications.color && selectedCategory1 === 'Beauty & Personal Care') {
            // Handle the case where only size is available for Beauty category
            let sizeObject = {
                size: specifications.size,
                id: toCamelCase(specifications.size),
                mrp: '',
                sellingPrice: '',
                primary: true
            };

            if (sizeVariants.some(variant => variant.primary)) {
                const notPrimaries = sizeVariants.filter(variant => !variant.primary);
                const filteredArray = sizeVariants.filter(variant => variant.primary);

                setSizeVariants([
                    ...notPrimaries,
                    { ...filteredArray[0], size: specifications.size, id: toCamelCase(specifications.size) }
                ]);
            }
            else {
                setSizeVariants([ ...sizeVariants, sizeObject ]);
            }

            // Remove color variants if present
            if (colorVariants.some(variant => variant.primary)) {
                const notPrimaries = colorVariants.filter(variant => !variant.primary);
                setColorVariants(notPrimaries);
            }
        }
        else {
            // Remove primary if present in arrays
            if (sizeVariants.some(variant => variant.primary)) {
                const notPrimaries = sizeVariants.filter(variant => !variant.primary);
                setSizeVariants(notPrimaries);
            }

            if (colorVariants.some(variant => variant.primary)) {
                const notPrimaries = colorVariants.filter(variant => !variant.primary);
                setColorVariants(notPrimaries);
            }
        }

        // eslint-disable-next-line
    }, [specifications.size, specifications.color]);

    useEffect(() => {
        const generateVariants = (sizeVariants, colorVariants) => {
            const combinedVariants = [];

            // Check if we have size variants for Beauty category (with or without color)
            if (selectedCategory1 === 'Beauty & Personal Care' && sizeVariants.length > 0) {
                sizeVariants.forEach(size => {
                    if (colorVariants.length === 0) {
                        // Only size variants, no color
                        combinedVariants.push({
                            mrp: size.mrp,
                            sellingPrice: size.sellingPrice,
                            size: size.size,
                            stock: "",
                            discount: "",
                            id: `${size.id}`,
                            Sku: ''
                        });
                    }
                    else {
                        // Both size and color variants
                        colorVariants.forEach(color => {
                            combinedVariants.push({
                                mrp: size.mrp,
                                sellingPrice: size.sellingPrice,
                                discount: "",
                                size: size.size,
                                color: color.color,
                                images: color.images,
                                variantName: color.variantName,
                                stock: "",
                                id: `${size.id}-${color.id}-form`,
                                Sku: ''
                            });
                        });
                    }
                });
            }
            else if (sizeVariants.length > 0) {
                // Handle other categories
                sizeVariants.forEach(size => {
                    if (colorVariants.length === 0) {
                        combinedVariants.push({
                            mrp: size.mrp,
                            sellingPrice: size.sellingPrice,
                            size: size.size,
                            stock: "",
                            discount: "",
                            id: `${size.id}`,
                            Sku: ''
                        });
                    }
                    else {
                        colorVariants.forEach(color => {
                            combinedVariants.push({
                                mrp: size.mrp,
                                sellingPrice: size.sellingPrice,
                                discount: "",
                                size: size.size,
                                color: color.color,
                                images: color.images,
                                variantName: color.variantName,
                                stock: "",
                                id: `${size.id}-${color.id}-form`,
                                Sku: ''
                            });
                        });
                    }
                });
            }

            return combinedVariants;
        };

        const finalVariants = generateVariants(sizeVariants, colorVariants);

        if(inventoryState.length > 0) {
            inventoryState.forEach(item => {
                const variant = finalVariants.find(variant => variant.id.includes(item.id));

                if (variant) {
                    variant.stock = item.stock;
                    variant.Sku = item.Sku;
                }
            });
        }
        else if(inventory.length > 0) {
            inventory.forEach(item => {
                const variant = finalVariants.find(variant => variant.id.includes(item.id));

                if (variant) {
                    variant.stock = item.stock;
                    variant.Sku = item.Sku;
                }
            });
        }

        setInventory(finalVariants);
        setInventoryState(finalVariants);

        // eslint-disable-next-line
    }, [sizeVariants, colorVariants, selectedCategory1]);

    const changeHandlerForInventoryVariant = (e) => {
        const { value, name, id } = e.target;

        if (value < 0) {
            showToast('Stock can\'t be negative.', 'warning');
            return;
        }
        else {
            setInventoryState(prevVariants =>
                prevVariants.map(variant => 
                    variant.id === id ? { ...variant, [name]: value } : variant
                )
            );

            setInventory(prevVariants =>
                prevVariants.map(variant =>
                    variant.id === id ? { ...variant, [name]: value } : variant
                )
            );
        }
    };

    useEffect(() => {
        if(inventoryState.length > 0) {
            const totalStock = inventoryState.reduce((acc, item) => acc + Number(item.stock), 0);
            setInventoryInputs({ ...inventoryInputs, stock: totalStock });
        }
        // eslint-disable-next-line
    }, [inventoryState]);

    useEffect(() => {
        if(displayStockTooltip) {
            setVisible(true);
        } else {
            setVisible(false);
        }
    }, [displayStockTooltip]);

    function handleChange(e) {
        const { name, value } = e.target;
        if(value < 0) {
            showToast('Negative value is not allowed', 'warning');
            return;
        }
        setInventoryInputs({
            ...inventoryInputs,
            [name]: value,
        });
    }

    useEffect(() => {
        if(inventoryState.length === 0) {
            setInventoryInputs({
                ...inventoryInputs,
                stock: '',
            })
        }
        // eslint-disable-next-line
    }, [inventoryState])

    return (
        <div className="flex flex-col gap-4 border border-[#EAEAEA] p-5 rounded-xl">
            <h1 className='font-semibold text-[18px] leading-[26px]'>Inventory</h1>

            <div className={"grid-cols-1 grid  gap-x-2 gap-y-4"}>
                <div className="flex flex-col gap-2">
                    {
                        inventoryState.length > 0 ? 
                        <>
                            <div className="flex items-center justify-between">
                                <h1 className='select-none cursor-pointer w-fit font-medium text-base text-[#121212]'>Total Quantity Available<span className="text-[#DC2626]">*</span></h1>
                            
                                {
                                    formErrors.stock && 
                                    <p className='text-[14px] leading-5 text-[#DC2626] select-none'>{formErrors.stock}</p>
                                }
                            </div>

                            <p className='px-3 py-4 bg-[#F5F5F5] border border-[#EAEAEA] rounded-[9px] font-normal text-base text-[#121212] select-none'>{inventoryInputs.stock}</p>
                        </> :
                        <Input 
                            labelText='Total Quantity Available' 
                            errorText={formErrors.stock} 
                            inputOptions={{
                                type: 'number',
                                id: 'stock',
                                name: 'stock',
                                value: inventoryInputs.stock,
                                onChange: handleChange,
                                placeholder:'Enter quantity'
                            }}
                            required
                        />
                    }
                </div>
            </div>

            {inventoryState.length > 0 && 
                <div className="border border-[#EAEAEA] rounded-lg">
                    <div className="w-full bg-[#F5F5F5] flex rounded-tl-lg rounded-tr-lg">
                        <div className="w-full flex">
                            <div className={`${colorVariants.length > 0 ? 'w-1/4' : 'w-1/3'} px-[10px] py-2 flex items-center`}>
                                <p className='font-semibold text-base text-[#121212] w-fit'>Size</p>
                            </div>

                            {colorVariants.length > 0 && 
                                <div className="w-1/4 px-[10px] py-2 flex items-center">
                                    <p className='font-semibold text-base text-[#121212] w-fit'>Color</p>
                                </div>
                            }

                            <div className={`${colorVariants.length > 0 ? 'w-1/4' : 'w-1/3'} px-[10px] py-2 flex items-center gap-[10px]`}>
                                <p className='font-semibold text-base text-[#121212] w-fit'>Stock in each</p>

                                <div className="relative w-6 h-6 flex items-center justify-center">
                                    <img
                                        className='cursor-pointer'
                                        src={Info} 
                                        alt="" 
                                        onMouseEnter={() => setDisplayStockTooltip(true)} 
                                        onMouseLeave={() => setDisplayStockTooltip(false)} 
                                    />

                                    {
                                        displayStockTooltip && 
                                        <div className={`${visible ? 'opacity-100 visible' : 'opacity-0 invisible'} transition-all delay-500 ease-in-out absolute z-20 top-6 left-[20%]`}>
                                            <div 
                                                className="
                                                    w-0 h-0 
                                                    border-l-[8px] border-l-transparent 
                                                    border-r-[8px] border-r-transparent 
                                                    border-b-[10px] border-b-[#000000]
                                                "
                                            ></div>
                                            <div className="relative right-1/2 w-[320px] p-3 bg-[#000000] rounded-lg">
                                                <p className='font-normal text-[12px] leading-5 text-[#FFFFFF]'>This is required field. If different sizes have different stock sizes, specify them here.</p>
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>

                            <div className={`${colorVariants.length > 0 ? 'w-1/4' : 'w-1/3'} px-[10px] py-2 flex items-center`}>
                                <p className='font-semibold text-base text-[#121212] w-fit'>SKU Code</p>
                            </div>
                        </div>
                    </div>

                    {inventoryState.map(variant => {
                        return (
                            <div key={variant.id} className="w-full bg-[#FFFFFF] flex items-center border-t border-[#EAEAEA]">
                                <div className="w-full flex">
                                    <div className={`${colorVariants.length > 0 ? 'w-1/4' : 'w-1/3'} px-[10px] py-[18px]`}>
                                        <p className='font-medium text-[14px] leading-6 text-[#121212] select-none'>{variant.size}</p>
                                    </div>

                                    {colorVariants.length > 0 && 
                                        <div className="w-1/4 px-[10px] py-[18px]">
                                            <p className='font-medium text-[14px] leading-6 text-[#121212] select-none'>{variant.color}</p>
                                        </div>
                                    }

                                    <input
                                        className={`${colorVariants.length > 0 ? 'w-1/4' : 'w-1/3'} outline-none px-[10px] py-[18px]`} 
                                        type="number" 
                                        placeholder='Eg: 5000' 
                                        id={variant.id} 
                                        name='stock' 
                                        value={variant.stock} 
                                        onChange={changeHandlerForInventoryVariant} 
                                    />

                                    <input
                                        className={`${colorVariants.length > 0 ? 'w-1/4' : 'w-1/3'} outline-none px-[10px] py-[18px]`} 
                                        type="text" 
                                        placeholder='Eg: UGG-BB-PUR-07' 
                                        id={variant.id} 
                                        name='Sku' 
                                        value={variant.Sku} 
                                        onChange={changeHandlerForInventoryVariant} 
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            }
        </div>
    );
}

export default Inventory;
